import { useMemo } from 'react';

import { createSearchCriteriaViewModel } from './searchCriteria/filterState';
import {
  clearAllSearchCriteria,
  clearAllSearchCriteriaFilters,
  clearSearchCriteriaFilter,
  clearSearchCriteriaQuery,
  getAppliedCriteria,
  setSearchCriteriaFilterValues,
  submitSearchCriteriaQuery,
  toggleSearchCriteriaFilterValue,
  type SearchCriteriaState,
} from './searchCriteria/state';
import type { UseSearchCriteriaStateResult } from './searchCriteria/types';
import type { NavigationMode, SearchPageCriteria, SearchPageFilterDefinition } from './sharedTypes';

type UseSearchPageCriteriaControllerOptions<FilterName extends string> = {
  criteriaState: UseSearchCriteriaStateResult<FilterName>;
  filterDefinitions: Record<FilterName, SearchPageFilterDefinition>;
  getCurrentCriteriaState: () => SearchCriteriaState<FilterName>;
  setNavigationMode: (mode: NavigationMode) => void;
  onCriteriaChange: (nextCriteria: SearchPageCriteria<FilterName>) => void;
  onClearAll?: (nextCriteria: SearchPageCriteria<FilterName>) => void;
};

export const useSearchPageCriteriaController = <FilterName extends string>({
  criteriaState,
  filterDefinitions,
  getCurrentCriteriaState,
  setNavigationMode,
  onCriteriaChange,
  onClearAll,
}: UseSearchPageCriteriaControllerOptions<FilterName>) => {
  const applyCriteriaChange = (
    mode: NavigationMode,
    nextCriteria: SearchPageCriteria<FilterName>,
    applyCriteriaStateChange: () => void
  ) => {
    setNavigationMode(mode);
    applyCriteriaStateChange();
    onCriteriaChange(nextCriteria);
  };

  const setFilterValues = (filterName: FilterName, values: string[]) => {
    const nextCriteria = getAppliedCriteria(
      setSearchCriteriaFilterValues(
        getCurrentCriteriaState(),
        filterDefinitions,
        filterName,
        values
      )
    );

    applyCriteriaChange('replace', nextCriteria, () => {
      criteriaState.setFilterValues(filterName, values);
    });
  };

  const toggleFilterValue = (filterName: FilterName, value: string) => {
    const nextCriteria = getAppliedCriteria(
      toggleSearchCriteriaFilterValue(
        getCurrentCriteriaState(),
        filterDefinitions,
        filterName,
        value
      )
    );

    applyCriteriaChange('replace', nextCriteria, () => {
      criteriaState.toggleFilterValue(filterName, value);
    });
  };

  const clearFilter = (filterName: FilterName) => {
    const nextCriteria = getAppliedCriteria(
      clearSearchCriteriaFilter(getCurrentCriteriaState(), filterName)
    );

    applyCriteriaChange('replace', nextCriteria, () => {
      criteriaState.clearFilter(filterName);
    });
  };

  const clearQuery = () => {
    const nextCriteria = getAppliedCriteria(clearSearchCriteriaQuery(getCurrentCriteriaState()));

    applyCriteriaChange('replace', nextCriteria, () => {
      criteriaState.clearQuery();
    });
  };

  const clearAllFilters = () => {
    const nextCriteria = getAppliedCriteria(
      clearAllSearchCriteriaFilters(getCurrentCriteriaState(), criteriaState.filterNames)
    );

    applyCriteriaChange('replace', nextCriteria, () => {
      criteriaState.clearAllFilters();
    });
  };

  const clearAll = () => {
    const nextCriteria = getAppliedCriteria(
      clearAllSearchCriteria(getCurrentCriteriaState(), criteriaState.filterNames)
    );

    setNavigationMode('replace');
    criteriaState.clearAll();

    if (onClearAll) {
      onClearAll(nextCriteria);
      return;
    }

    onCriteriaChange(nextCriteria);
  };

  const submitQuery = (nextQuery = criteriaState.queryDraft) => {
    const nextCriteria = getAppliedCriteria(
      submitSearchCriteriaQuery(getCurrentCriteriaState(), criteriaState.filterNames, nextQuery)
    );

    applyCriteriaChange('push', nextCriteria, () => {
      criteriaState.submitQuery(nextQuery);
    });
  };

  const viewModel = useMemo(
    () =>
      createSearchCriteriaViewModel({
        filterDefinitions,
        filterNames: criteriaState.filterNames,
        selectedFilters: criteriaState.selectedFilters,
        appliedQuery: criteriaState.query,
        setFilterValues,
        toggleFilterValue,
        clearFilter,
        clearQuery,
      }),
    [
      clearFilter,
      clearQuery,
      criteriaState.filterNames,
      criteriaState.query,
      criteriaState.selectedFilters,
      filterDefinitions,
      setFilterValues,
      toggleFilterValue,
    ]
  );

  return {
    ...viewModel,
    setFilterValues,
    toggleFilterValue,
    clearFilter,
    clearQuery,
    clearAllFilters,
    clearAll,
    submitQuery,
  };
};
